import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlusIcon, TrashIcon, CalendarIcon } from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import Button from "./ui/Button";
import Card from "./ui/Card";
import ParticipantRow from "./ParticipantRow";
import "react-datepicker/dist/react-datepicker.css";
import type { LessonDate, Participant } from "../../types/booking";
import { formatDateLocal } from "../../utils/helpers";
import LateBookingModal from "./LateBookingModal";

interface ParticipantErrors {
  name?: { message?: string };
  age?: { message?: string };
  activity?: { message?: string };
  timeSlot?: { message?: string };
  skillLevel?: { message?: string };
  lessonType?: { message?: string };
}

const toStartOfDay = (value?: string | null): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
};

const NO_OVERLAP_MESSAGE =
  "No overlapping availability for the selected lesson types. Please adjust the lesson types or participants.";

const getOrdinalLessonNumber = (num: number) => {
  const ordinals = [
    "first",
    "second",
    "third",
    "fourth",
    "fifth",
    "sixth",
    "seventh",
    "eighth",
    "ninth",
    "tenth",
  ];
  if (num >= 1 && num <= 10) {
    return ordinals[num - 1];
  }
  const j = num % 10,
    k = num % 100;
  if (j == 1 && k != 11) {
    return num + "st";
  }
  if (j == 2 && k != 12) {
    return num + "nd";
  }
  if (j == 3 && k != 13) {
    return num + "rd";
  }
  return num + "th";
};

interface LessonDateCardProps {
  lessonDate: LessonDate;
  index: number;
  canRemove: boolean;
  onUpdate: (lessonDateId: string, updates: Partial<LessonDate>) => void;
  onRemove: (lessonDateId: string) => void;
  onAddParticipant: (lessonDateId: string, sharedInstructor?: 0 | 1) => void;
  onRemoveParticipant: (lessonDateId: string, participantId: string) => void;
  onUpdateParticipant: (
    lessonDateId: string,
    participantId: string,
    updates: Partial<Participant>,
  ) => void;
  excludeDates?: Date[];
  errors?: {
    date?: { message?: string };
    participants?: Array<ParticipantErrors>;
  };
  onAddLessonDate?: () => void;
  isLast: boolean;
}

const LessonDateCard: React.FC<LessonDateCardProps> = ({
  lessonDate,
  index,
  canRemove,
  onUpdate,
  onRemove,
  onAddParticipant,
  onRemoveParticipant,
  onUpdateParticipant,
  excludeDates = [],
  errors,
  onAddLessonDate,
  isLast,
}) => {
  const [showLateBookingModal, setShowLateBookingModal] = useState(false);
  const [showSharedInstructorModal, setShowSharedInstructorModal] = useState(false);

  // Calculate the first valid booking date (Today + 2 days)
  const validBookingStartDate = React.useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const defaultMinDate = React.useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  }, []);

  const availabilityRange = React.useMemo(() => {
    const normalized = lessonDate.participants
      .map((participant) => participant.lessonTypeAvailability)
      .filter(
        (
          availability,
        ): availability is NonNullable<Participant["lessonTypeAvailability"]> =>
          Boolean(availability?.periodStart && availability?.periodEnd),
      )
      .map((availability) => {
        const startDate = toStartOfDay(availability.periodStart);
        const endDate = toStartOfDay(availability.periodEnd);
        if (!startDate || !endDate) {
          return null;
        }
        return { startDate, endDate };
      })
      .filter((range): range is { startDate: Date; endDate: Date } =>
        Boolean(range),
      );

    if (!normalized.length) {
      return null;
    }

    const startDate = normalized.reduce<Date | null>(
      (latest, current) =>
        !latest || current.startDate > latest ? current.startDate : latest,
      null,
    );

    const endDate = normalized.reduce<Date | null>(
      (earliest, current) =>
        !earliest || current.endDate < earliest ? current.endDate : earliest,
      null,
    );

    if (!startDate || !endDate) {
      return null;
    }

    return { startDate, endDate };
  }, [lessonDate.participants]);

  const hasOverlap =
    availabilityRange?.startDate && availabilityRange?.endDate
      ? availabilityRange.startDate <= availabilityRange.endDate
      : true;

  const minSelectableDate = React.useMemo(() => {
    if (!availabilityRange) {
      return defaultMinDate;
    }
    const candidate = new Date(
      Math.max(defaultMinDate.getTime(), availabilityRange.startDate.getTime()),
    );
    candidate.setHours(0, 0, 0, 0);
    return candidate;
  }, [availabilityRange, defaultMinDate]);

  const maxSelectableDate =
    availabilityRange && hasOverlap ? availabilityRange.endDate : undefined;

  const showNoOverlap = Boolean(availabilityRange) && !hasOverlap;

  const handleDateChange = (date: Date | null) => {
    if (!date) {
      onUpdate(lessonDate.id, { date: null });
      return;
    }

    // Check if the selected date is too early (Today or Tomorrow)
    if (date < validBookingStartDate) {
      setShowLateBookingModal(true);
      return;
    }

    onUpdate(lessonDate.id, { date });
  };

  const handleAddParticipantClick = () => {
    onAddParticipant(lessonDate.id);
    if (lessonDate.participants.length === 1) {
      setShowSharedInstructorModal(true);
    }
  };

  return (
    <>
      <Card className="p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
        >
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <CalendarIcon className="h-6 w-6 text-blue-600" />
              <h3 className="text-lg font-semibold text-gray-900">
                Lesson {index + 1} Participants
              </h3>
            </div>

            <div className="flex items-center gap-2">
              {canRemove && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(lessonDate.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <TrashIcon className="h-5 w-5 mr-2" />
                  Remove Date
                </Button>
              )}
            </div>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select the date for your {getOrdinalLessonNumber(index + 1)} lesson <span className="text-red-500">*</span>
            </label>
            {showNoOverlap && (
              <p className="text-sm text-red-600 mb-2">{NO_OVERLAP_MESSAGE}</p>
            )}

            <div>
              <DatePicker
                selected={lessonDate.date}
                onChange={handleDateChange}
                dateFormat="yyyy-MM-dd (EEEE)"
                minDate={minSelectableDate}
                maxDate={maxSelectableDate}
                excludeDates={excludeDates}
                disabled={showNoOverlap}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 h-10 w-full sm:w-[14rem]"
                placeholderText="Select date"
              />
              <p className="text-xs text-gray-500 mt-1">
                Online bookings must be made at least 2 days in advance.
              </p>
              {errors?.date?.message && (
                <p className="text-sm text-red-600 mt-1">
                  {errors.date.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4 mb-6">
            {lessonDate.participants.map((participant, participantIndex) => (
              <ParticipantRow
                key={participant.id}
                participant={participant}
                index={participantIndex}
                canRemove={lessonDate.participants.length > 1}
                onUpdate={(updates) =>
                  onUpdateParticipant(lessonDate.id, participant.id, updates)
                }
                onRemove={() =>
                  onRemoveParticipant(lessonDate.id, participant.id)
                }
                errors={errors?.participants?.[participantIndex]}
                lessonDate={
                  lessonDate.date ? formatDateLocal(lessonDate.date) : null
                }
              />
            ))}
          </div>

          {errors?.participants && errors.participants.length > 0 && (
            <p className="text-sm text-red-600 mb-4">
              Please fix the participant errors above
            </p>
          )}
          <div className="space-y-3">
            <Button
              variant="outline"
              size="lg"
              onClick={handleAddParticipantClick}
              className="w-full font-bold text-sm md:text-xl h-auto py-3 md:h-14 whitespace-normal"
            >
              <PlusIcon className="h-5 w-5 md:h-7 md:w-7 mr-2 shrink-0" />
              Add another participant on this date
            </Button>

            {isLast && onAddLessonDate && (
              <Button
                variant="outline"
                onClick={onAddLessonDate}
                className="w-full text-red-600 border-red-500 hover:bg-red-50 hover:border-red-600 font-bold text-sm md:text-xl h-auto py-3 md:h-14 whitespace-normal"
              >
                <PlusIcon className="h-5 w-5 md:h-7 md:w-7 mr-2 shrink-0" />
                Add lessons on another date (for multiple day bookings)
              </Button>
            )}
          </div>
        </motion.div>
      </Card>
      <LateBookingModal
        isOpen={showLateBookingModal}
        onClose={() => setShowLateBookingModal(false)}
      />

      <AnimatePresence>
        {showSharedInstructorModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full"
            >
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">Instructor Request</h2>
                <button
                  onClick={() => setShowSharedInstructorModal(false)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="p-6">
                <p className="text-gray-600 mb-6 text-lg">
                  If you would like more than one instructor for your group, please write in the comments what you require.
                </p>

                <div className="flex flex-col space-y-3">
                  <Button
                    variant="primary"
                    onClick={() => {
                      setShowSharedInstructorModal(false);
                      setTimeout(() => {
                        document.getElementById('comments-section')?.scrollIntoView({ behavior: 'smooth' });
                      }, 100);
                    }}
                  >
                    Take me to Comments
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowSharedInstructorModal(false);
                    }}
                  >
                    OK, got it
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LessonDateCard;
